/* import __COLOCATED_TEMPLATE__ from './emoji-action.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { type ComposerPublicAPI } from '@intercom/composer-ember';
import storage from 'embercom/vendor/intercom/storage';
import { type DisplayContext } from 'embercom/services/command-k';
import type CustomEmojiService from 'embercom/services/custom-emoji-service';
import type { CustomEmoji } from 'embercom/services/custom-emoji-service';
import { SkinToneModifiers } from 'embercom/services/emoji-service';

// @ts-ignore
import intermoji from '@intercom/intermoji';

interface Args {
  composerApi?: ComposerPublicAPI;
  priority?: number;
  trackingData?: Record<string, unknown>;
  activatingComponent?: `${DisplayContext}`;
}

interface Signature {
  Args: Args;
}

interface EmojiUsage {
  emoji: string;
  count: number;
}

let FREQUENTLY_USED_STORAGE_KEY = 'frequentlyUsedEmojis';

export default class EmojiAction extends Component<Signature> {
  @service declare customEmojiService: CustomEmojiService;
  @service declare intercomEventService: $TSFixMe;

  @action insertEmoji(emojiValue: string | CustomEmoji) {
    // standard emojis can be added as text
    // custom emojis need to be added as an inline image
    if ((emojiValue as CustomEmoji).isCustomEmoji) {
      this.trackCustomEmojiInsertion(emojiValue as CustomEmoji);
      this.insertCustomEmoji(emojiValue as CustomEmoji);
    } else {
      this.insertStandardEmoji(emojiValue as string);
    }
  }

  private trackCustomEmojiInsertion(customEmoji: CustomEmoji): void {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'custom_emoji',
      section: 'composer',
      emoji_identifier: customEmoji.identifier,
      emoji_type: 'custom',
      insertion_method: 'picker',
    });
  }

  private insertCustomEmoji(customEmoji: CustomEmoji) {
    // Insert custom emoji as inline image
    let composer = this.args.composerApi?.composer;
    if (composer) {
      let state = composer.state.prosemirrorState;

      let inlineImageNode = state.schema.nodes.inlineImage.create({
        src: customEmoji.src,
        text: customEmoji.alt || customEmoji.identifier,
        type: 'inlineImage',
        className: 'custom-emoji',
      });

      let tr = state.tr.replaceSelectionWith(inlineImageNode);
      composer.commands.dispatch(tr);

      composer.commands.focus();
    }

    // Store usage for frequently used emoji category
    this.persistCustomEmoji(customEmoji);
  }

  private insertStandardEmoji(emoji: string) {
    this.persistEmoji(emoji);
    this.args.composerApi?.composer.commands.focus();
    this.args.composerApi?.composer.commands.insertText(emoji);
  }

  private persistCustomEmoji(customEmoji: CustomEmoji) {
    this.trackEmojiUsage(customEmoji.identifier);
  }

  private persistEmoji(emoji: string) {
    // Convert emoji to identifier (skin-tone neutral) before storing
    let identifier = this.getEmojiIdentifier(emoji);
    this.trackEmojiUsage(identifier);
  }

  /**
   * Tracks emoji usage with frequency counting.
   * Stores emojis sorted by usage count (most frequently used first).
   */
  private trackEmojiUsage(identifier: string) {
    let emojiUsageData = this.getEmojiUsageData();

    // Find existing emoji and increment count, or add new one
    let existingIndex = emojiUsageData.findIndex((item) => item.emoji === identifier);
    if (existingIndex >= 0) {
      emojiUsageData[existingIndex].count++;
    } else {
      emojiUsageData.push({ emoji: identifier, count: 1 });
    }

    // Sort by count (most frequently used first)
    emojiUsageData.sort((a, b) => b.count - a.count);

    storage.set(FREQUENTLY_USED_STORAGE_KEY, emojiUsageData);
  }

  /**
   * Gets emoji usage data from storage.
   * Returns empty array if no storage exists (will be populated as user selects emojis).
   */
  private getEmojiUsageData(): EmojiUsage[] {
    let stored = storage.get(FREQUENTLY_USED_STORAGE_KEY);

    // Storage exists - validate and return it
    if (stored && Array.isArray(stored) && stored.length > 0) {
      // Validate and filter data to ensure it matches EmojiUsage format
      return stored.filter(
        (item): item is EmojiUsage =>
          typeof item === 'object' &&
          item !== null &&
          'emoji' in item &&
          'count' in item &&
          typeof item.emoji === 'string' &&
          typeof item.count === 'number',
      );
    }

    return [];
  }

  /**
   * Converts emoji to its identifier for storage.
   * Identifiers are skin-tone neutral and preserve variation selectors.
   * Falls back to unicode for emojis not in intermoji database.
   */
  private getEmojiIdentifier(emoji: string): string {
    // Remove skin tone modifiers while preserving variation selectors
    let baseEmoji = this.removeSkintoneModifiers(emoji);

    // Then convert the base emoji to identifier (now skin-tone neutral)
    let identifier = intermoji.identifierFromUnicode(baseEmoji);

    if (identifier) {
      // Found it: 'snowflake', 'thumbs_up', 'skier', etc. (without skin tone)
      return identifier;
    }

    // Not in intermoji database (like 🥾 hiking boot)
    // Fall back to storing the unicode emoji as-is
    return emoji;
  }

  /**
   * Removes skin tone modifiers from an emoji while preserving variation selectors.
   * Skin tone modifiers are U+1F3FB through U+1F3FF.
   */
  private removeSkintoneModifiers(emoji: string): string {
    let skinToneModifiers = [
      SkinToneModifiers.LightSkinToneModifier,
      SkinToneModifiers.MediumLightSkinToneModifier,
      SkinToneModifiers.MediumSkinToneModifier,
      SkinToneModifiers.MediumDarkSkinToneModifier,
      SkinToneModifiers.DarkSkinToneModifier,
    ];

    // Convert emoji to array of code points
    let codePoints = Array.from(emoji)
      .map((char) => char.codePointAt(0))
      .filter((cp): cp is number => cp !== undefined);

    // Filter out skin tone modifiers
    let filteredCodePoints = codePoints.filter((cp) => !skinToneModifiers.includes(cp));

    // Convert back to string with error handling
    try {
      return String.fromCodePoint(...filteredCodePoints);
    } catch (e) {
      // If invalid code points, return original emoji
      return emoji;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Composer::CommandK::EmojiAction': typeof EmojiAction;
    'inbox2/composer/command-k/emoji-action': typeof EmojiAction;
  }
}
